import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, Wand2, Trash2, Eye, EyeOff, ChevronDown, Users, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PuzzleManager() {
  const [puzzles, setPuzzles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genType, setGenType] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [participants, setParticipants] = useState({});
  const [loadingParts, setLoadingParts] = useState('');
  const [editing, setEditing] = useState(null); // {id, ...fields}
  const [creating, setCreating] = useState(false);
  const [newPuzzle, setNewPuzzle] = useState({ puzzle_type: 'riddle', question: '', answer: '', hint: '', scrambled: '', question_ja: '', answer_ja: '', hint_ja: '', scrambled_ja: '' });

  const fetchPuzzles = async () => {
    try {
      const { data } = await axios.get(`${API}/api/puzzle/admin`, { withCredentials: true });
      setPuzzles(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchPuzzles(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/puzzle/generate`, { puzzle_type: genType || null }, { withCredentials: true });
      setNewPuzzle({
        puzzle_type: data.puzzle_type,
        question: data.question, answer: data.answer, hint: data.hint, scrambled: data.scrambled || '',
        question_ja: data.question_ja || '', answer_ja: data.answer_ja || '', hint_ja: data.hint_ja || '', scrambled_ja: data.scrambled_ja || '',
      });
      setCreating(true);
    } catch {}
    setGenerating(false);
  };

  const savePuzzle = async () => {
    try {
      await axios.post(`${API}/api/puzzle/create`, newPuzzle, { withCredentials: true });
      setCreating(false);
      setNewPuzzle({ puzzle_type: 'riddle', question: '', answer: '', hint: '', scrambled: '', question_ja: '', answer_ja: '', hint_ja: '', scrambled_ja: '' });
      fetchPuzzles();
    } catch {}
  };

  const activate = async (id) => {
    await axios.put(`${API}/api/puzzle/${id}/activate`, {}, { withCredentials: true });
    fetchPuzzles();
  };

  const deactivate = async (id) => {
    await axios.put(`${API}/api/puzzle/${id}/deactivate`, {}, { withCredentials: true });
    fetchPuzzles();
  };

  const deletePuzzle = async (id) => {
    try {
      await axios.delete(`${API}/api/puzzle/${id}`, { withCredentials: true });
      setPuzzles(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      alert('Failed to delete: ' + (e.response?.data?.detail || e.message));
    }
  };

  const loadParticipants = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setLoadingParts(id);
    try {
      const { data } = await axios.get(`${API}/api/puzzle/${id}/participants`, { withCredentials: true });
      setParticipants(prev => ({ ...prev, [id]: data }));
    } catch {}
    setLoadingParts('');
    setExpandedId(id);
  };

  const saveEdit = async () => {
    if (!editing) return;
    await axios.put(`${API}/api/puzzle/${editing.id}`, editing, { withCredentials: true });
    setEditing(null);
    fetchPuzzles();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={28} className="text-brand animate-spin" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl tracking-wider uppercase text-white mb-1">Weekly Puzzle</h2>
          <p className="font-sans text-sm text-neutral-400">Create, generate, and manage weekly puzzles. Track participants.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setCreating(true); setNewPuzzle({ puzzle_type: 'riddle', question: '', answer: '', hint: '', scrambled: '', question_ja: '', answer_ja: '', hint_ja: '', scrambled_ja: '' }); }} data-testid="puzzle-create-btn" className="flex items-center gap-1.5 font-heading text-xs tracking-widest uppercase bg-neutral-800 text-white px-4 py-2.5 rounded-lg hover:bg-neutral-700 transition-colors">
            <Plus size={14} /> Create
          </button>
        </div>
      </div>

      {/* AI Generate */}
      <div className="border border-neutral-800 rounded-xl p-5 bg-neutral-900/50" data-testid="ai-generate-section">
        <h3 className="font-heading text-sm tracking-widest uppercase text-neutral-300 mb-3">AI Puzzle Generator</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={genType} onChange={e => setGenType(e.target.value)} data-testid="gen-type-select" className="bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white">
            <option value="">Random Type</option>
            <option value="riddle">Riddle</option>
            <option value="word_scramble">Word Scramble</option>
            <option value="trivia">Trivia</option>
          </select>
          <button onClick={generate} disabled={generating} data-testid="gen-puzzle-btn" className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-brand text-white px-5 py-2.5 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50">
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            {generating ? 'Generating...' : 'Generate Puzzle'}
          </button>
        </div>
      </div>

      {/* Create / Edit form */}
      {creating && (
        <div className="border-2 border-brand/40 rounded-xl p-5 bg-brand/5 space-y-4" data-testid="puzzle-create-form">
          <h3 className="font-heading text-sm tracking-widest uppercase text-brand">
            {newPuzzle.question ? 'Review & Save Puzzle' : 'Create New Puzzle'}
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mb-1 block">Type</label>
              <select value={newPuzzle.puzzle_type} onChange={e => setNewPuzzle(p => ({ ...p, puzzle_type: e.target.value }))} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white">
                <option value="riddle">Riddle</option>
                <option value="word_scramble">Word Scramble</option>
                <option value="trivia">Trivia</option>
              </select>
            </div>
            <div>
              <label className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mb-1 block">Answer</label>
              <input value={newPuzzle.answer} onChange={e => setNewPuzzle(p => ({ ...p, answer: e.target.value }))} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="Correct answer" />
            </div>
          </div>
          <div>
            <label className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mb-1 block">Question</label>
            <textarea value={newPuzzle.question} onChange={e => setNewPuzzle(p => ({ ...p, question: e.target.value }))} rows={3} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white resize-none" placeholder="The puzzle question..." />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mb-1 block">Hint</label>
              <input value={newPuzzle.hint} onChange={e => setNewPuzzle(p => ({ ...p, hint: e.target.value }))} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="Optional hint" />
            </div>
            {newPuzzle.puzzle_type === 'word_scramble' && (
              <div>
                <label className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mb-1 block">Scrambled Letters</label>
                <input value={newPuzzle.scrambled} onChange={e => setNewPuzzle(p => ({ ...p, scrambled: e.target.value }))} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="e.g. KSYCOL" />
              </div>
            )}
          </div>

          {/* --- Japanese (JA) version --- */}
          <div className="border-t border-brand/20 pt-4 mt-2">
            <h4 className="font-heading text-[11px] tracking-widest uppercase text-brand/80 mb-3">日本語版 (Japanese Version)</h4>
            <div className="grid sm:grid-cols-2 gap-4 mb-3">
              <div>
                <label className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mb-1 block">答え (Answer JA)</label>
                <input value={newPuzzle.answer_ja} onChange={e => setNewPuzzle(p => ({ ...p, answer_ja: e.target.value }))} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="日本語の答え" />
              </div>
              <div>
                <label className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mb-1 block">ヒント (Hint JA)</label>
                <input value={newPuzzle.hint_ja} onChange={e => setNewPuzzle(p => ({ ...p, hint_ja: e.target.value }))} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="日本語のヒント" />
              </div>
            </div>
            <div className="mb-3">
              <label className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mb-1 block">質問 (Question JA)</label>
              <textarea value={newPuzzle.question_ja} onChange={e => setNewPuzzle(p => ({ ...p, question_ja: e.target.value }))} rows={3} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white resize-none" placeholder="日本語の質問..." />
            </div>
            {newPuzzle.puzzle_type === 'word_scramble' && (
              <div>
                <label className="font-heading text-[11px] tracking-widest uppercase text-neutral-500 mb-1 block">並べ替え文字 (Scrambled JA)</label>
                <input value={newPuzzle.scrambled_ja} onChange={e => setNewPuzzle(p => ({ ...p, scrambled_ja: e.target.value }))} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="例: コツカイ" />
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={savePuzzle} data-testid="puzzle-save-btn" className="font-heading text-xs tracking-widest uppercase bg-brand text-white px-5 py-2.5 rounded-lg hover:bg-brand-hover transition-colors">Save Puzzle</button>
            <button onClick={() => setCreating(false)} className="font-heading text-xs tracking-widest uppercase text-neutral-400 px-4 py-2.5 hover:text-white transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Puzzle list */}
      <div className="space-y-3">
        {puzzles.length === 0 && !creating && (
          <div className="text-center py-12 border-2 border-dashed border-neutral-800 rounded-xl">
            <p className="font-heading text-sm tracking-widest uppercase text-neutral-600">No puzzles yet — generate or create one above</p>
          </div>
        )}
        {puzzles.map(p => (
          <PuzzleRow
            key={p.id}
            puzzle={p}
            expanded={expandedId === p.id}
            participants={participants[p.id] || []}
            loadingParts={loadingParts === p.id}
            editing={editing?.id === p.id ? editing : null}
            onActivate={() => activate(p.id)}
            onDeactivate={() => deactivate(p.id)}
            onDelete={() => deletePuzzle(p.id)}
            onExpand={() => loadParticipants(p.id)}
            onEdit={() => setEditing({ id: p.id, question: p.question, answer: p.answer, hint: p.hint, scrambled: p.scrambled, puzzle_type: p.puzzle_type, question_ja: p.question_ja || '', answer_ja: p.answer_ja || '', hint_ja: p.hint_ja || '', scrambled_ja: p.scrambled_ja || '' })}
            onEditChange={(field, val) => setEditing(prev => ({ ...prev, [field]: val }))}
            onEditSave={saveEdit}
            onEditCancel={() => setEditing(null)}
          />
        ))}
      </div>
    </div>
  );
}

function PuzzleRow({ puzzle, expanded, participants, loadingParts, editing, onActivate, onDeactivate, onDelete, onExpand, onEdit, onEditChange, onEditSave, onEditCancel }) {
  const typeLabel = { riddle: 'Riddle', word_scramble: 'Scramble', trivia: 'Trivia' }[puzzle.puzzle_type] || puzzle.puzzle_type;
  const [confirmDel, setConfirmDel] = useState(false);

  return (
    <div data-testid={`puzzle-row-${puzzle.id}`} className={`border rounded-xl overflow-hidden ${puzzle.active ? 'border-brand/50 bg-brand/5' : 'border-neutral-800 bg-neutral-900/50'}`}>
      <div className="p-4 flex items-center gap-3">
        {/* Status dot */}
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${puzzle.active ? 'bg-green-500' : 'bg-neutral-600'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading text-[11px] tracking-widest uppercase px-2 py-0.5 rounded bg-neutral-800 text-neutral-300">{typeLabel}</span>
            {puzzle.active && <span className="font-heading text-[11px] tracking-widest uppercase text-green-400">ACTIVE</span>}
            {puzzle.week_label && <span className="font-sans text-xs text-neutral-600">{puzzle.week_label}</span>}
          </div>
          <p className="font-sans text-sm text-white mt-1 truncate">{puzzle.question}</p>
          <p className="font-sans text-xs text-neutral-500 mt-0.5">Answer: <span className="text-neutral-400">{puzzle.answer}</span> · {puzzle.participants} entries · {puzzle.correct_count} correct</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={onExpand} data-testid={`puzzle-expand-${puzzle.id}`} className="p-2 rounded-lg hover:bg-neutral-800 transition-colors text-neutral-400 hover:text-white" title="View participants">
            <Users size={15} />
          </button>
          {puzzle.active ? (
            <button onClick={onDeactivate} className="p-2 rounded-lg hover:bg-neutral-800 transition-colors text-green-400 hover:text-yellow-400" title="Deactivate">
              <EyeOff size={15} />
            </button>
          ) : (
            <button onClick={onActivate} data-testid={`puzzle-activate-${puzzle.id}`} className="p-2 rounded-lg hover:bg-neutral-800 transition-colors text-neutral-400 hover:text-green-400" title="Set as active">
              <Eye size={15} />
            </button>
          )}
          <button onClick={onEdit} className="p-2 rounded-lg hover:bg-neutral-800 transition-colors text-neutral-400 hover:text-white" title="Edit">
            <ChevronDown size={15} />
          </button>
          {confirmDel ? (
            <div className="flex items-center gap-1">
              <button onClick={() => { onDelete(); setConfirmDel(false); }} data-testid={`puzzle-confirm-delete-${puzzle.id}`} className="font-heading text-[10px] tracking-widest uppercase bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-500 transition-colors">Delete</button>
              <button onClick={() => setConfirmDel(false)} className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 px-2 py-1.5 hover:text-white transition-colors">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setConfirmDel(true)} data-testid={`puzzle-delete-${puzzle.id}`} className="p-2 rounded-lg hover:bg-red-900/30 transition-colors text-neutral-400 hover:text-red-400 min-w-[36px] min-h-[36px] flex items-center justify-center" title="Delete">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Inline edit */}
      {editing && (
        <div className="px-4 pb-4 border-t border-neutral-800 pt-3 space-y-3">
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">English</label>
            <textarea value={editing.question} onChange={e => onEditChange('question', e.target.value)} rows={2} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white resize-none" placeholder="Question (EN)" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input value={editing.answer} onChange={e => onEditChange('answer', e.target.value)} className="bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="Answer" />
            <input value={editing.hint} onChange={e => onEditChange('hint', e.target.value)} className="bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="Hint" />
            <input value={editing.scrambled || ''} onChange={e => onEditChange('scrambled', e.target.value)} className="bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="Scrambled" />
          </div>
          <div>
            <label className="font-heading text-[10px] tracking-widest uppercase text-brand/80 mb-1 block">日本語 (Japanese)</label>
            <textarea value={editing.question_ja || ''} onChange={e => onEditChange('question_ja', e.target.value)} rows={2} className="w-full bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white resize-none" placeholder="質問 (Question JA)" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input value={editing.answer_ja || ''} onChange={e => onEditChange('answer_ja', e.target.value)} className="bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="答え" />
            <input value={editing.hint_ja || ''} onChange={e => onEditChange('hint_ja', e.target.value)} className="bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="ヒント" />
            <input value={editing.scrambled_ja || ''} onChange={e => onEditChange('scrambled_ja', e.target.value)} className="bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white" placeholder="並べ替え" />
          </div>
          <div className="flex gap-2">
            <button onClick={onEditSave} className="font-heading text-xs tracking-widest uppercase bg-brand text-white px-4 py-2 rounded-lg hover:bg-brand-hover transition-colors">Save</button>
            <button onClick={onEditCancel} className="font-heading text-xs tracking-widest uppercase text-neutral-400 px-3 py-2 hover:text-white transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Participants list */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-neutral-800 pt-3">
          {loadingParts && <Loader2 size={16} className="text-brand animate-spin" />}
          {!loadingParts && participants.length === 0 && (
            <p className="font-sans text-xs text-neutral-600">No participants yet.</p>
          )}
          {!loadingParts && participants.length > 0 && (
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 border-b border-neutral-800">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Answer</th>
                    <th className="py-2 text-center">Correct</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map(pt => (
                    <tr key={pt.id} className="border-b border-neutral-800/50 font-sans text-xs text-neutral-300">
                      <td className="py-2 pr-3">{pt.name}</td>
                      <td className="py-2 pr-3 text-neutral-500">{pt.email}</td>
                      <td className="py-2 pr-3">{pt.answer}</td>
                      <td className="py-2 text-center">{pt.correct ? <Check size={14} className="text-green-400 mx-auto" /> : <X size={14} className="text-red-400 mx-auto" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
